import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

type Status = 'validating' | 'ready' | 'error';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>('validating');
  const [errorMsg, setErrorMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    const markReady = () => {
      if (cancelled || settled) return;
      settled = true;
      setStatus('ready');
    };

    const markError = (msg: string) => {
      if (cancelled || settled) return;
      settled = true;
      setErrorMsg(msg);
      setStatus('error');
    };

    // Listen for Supabase to detect the recovery session from the URL hash
    // (this fires after /verify redirects with #access_token=...&type=recovery)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        markReady();
      }
    });

    const run = async () => {
      // 1) Error in URL (Supabase redirected with ?error=... or #error=...)
      const hash = window.location.hash;
      const urlError = searchParams.get('error_description') || searchParams.get('error');
      const hashError = hash.includes('error') ? decodeURIComponent(hash.split('error_description=')[1]?.split('&')[0] ?? '') : '';
      if (urlError || hashError) {
        markError('This reset link is invalid or has expired. Please request a new one.');
        return;
      }

      // 2) PKCE flow: ?code=...
      const code = searchParams.get('code');
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          markError('This reset link is invalid or has expired. Please request a new one.');
        } else {
          markReady();
        }
        return;
      }

      // 3) Hash recovery flow: #access_token=...&type=recovery
      //    Supabase auto-detects this; PASSWORD_RECOVERY listener above handles it.
      //    Wait briefly, then fall back to checking the session directly.
      if (hash && (hash.includes('type=recovery') || hash.includes('access_token'))) {
        // Give Supabase up to 3s to parse the hash and emit PASSWORD_RECOVERY
        setTimeout(async () => {
          if (settled) return;
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            markReady();
          } else {
            markError('This reset link is invalid or has expired. Please request a new one.');
          }
        }, 1500);
        return;
      }

      // 4) Already authenticated in this tab
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        markReady();
      } else {
        markError('No reset link detected. Please request a new password reset email.');
      }
    };

    run();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [searchParams]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success('Password updated successfully!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {status === 'ready' ? 'Set a new password' : 'Reset your password'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'validating' && 'Validating your reset link…'}
            {status === 'ready' && 'Enter your new password below'}
            {status === 'error' && "We couldn't validate this link"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {status === 'validating' && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Please wait…
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">{errorMsg}</p>
              <Button
                type="button"
                className="w-full"
                onClick={() => navigate('/auth')}
              >
                Request a new reset link
              </Button>
            </div>
          )}

          {status === 'ready' && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Updating...' : 'Update password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
